export const INACTIVE_AFTER_MS = 3 * 60 * 60 * 1000;
export const RUNNING_SPEED_KMH = 5;

const canonicalizeKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const toTimestampMs = (value, fallback = 0) => {
  if (value == null || value === '') return fallback;
  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isFinite(ts) ? ts : fallback;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const parseIgnitionValue = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === 'number') return value !== 0;

  const key = canonicalizeKey(value);
  if (!key) return null;

  if (
    ['1', 'true', 'on', 'yes', 'acc_on', 'ignition_on', 'engine_on', 'engine_start', 'started', 'start', 'running'].includes(
      key,
    )
  ) {
    return true;
  }

  if (
    ['0', 'false', 'off', 'no', 'acc_off', 'ignition_off', 'engine_off', 'engine_stop', 'stopped', 'stop'].includes(
      key,
    )
  ) {
    return false;
  }

  return null;
};

export const normalizeCanonicalStatus = (value) => {
  const key = canonicalizeKey(value);
  if (!key) return null;
  if (key === 'moving') return 'running';
  if (key === 'idling') return 'idle';
  if (['parked', 'engine_off', 'ignition_off', 'acc_off', 'stop'].includes(key)) return 'stopped';
  if (['offline', 'inactive', 'disconnected', 'disconnect', 'no_active', 'not_reporting', 'no_reporting'].includes(key)) {
    return 'inactive';
  }
  if (['unknown', 'nodata', 'no_data'].includes(key)) return 'no_data';
  if (['running', 'idle', 'stopped', 'inactive', 'expired', 'no_data'].includes(key)) return key;
  return null;
};

export const resolveCanonicalDeviceStatus = ({
  status,
  vehicleStatus,
  movementStatus,
  movementState,
  ignition,
  accStatus,
  speed,
  lastPacketAt,
  hasEverReported,
  licenseExpired,
  now,
} = {}) => {
  const statusCandidate = normalizeCanonicalStatus(vehicleStatus) || normalizeCanonicalStatus(status);
  const nowMs = toTimestampMs(now, Date.now()) || Date.now();
  const lastPacketMs = toTimestampMs(lastPacketAt, 0);
  const ignitionValue = parseIgnitionValue(ignition ?? accStatus);
  const speedValue = Math.max(0, toFiniteNumber(speed, 0) || 0);
  const hasLiveMotionSignal =
    ignitionValue !== null ||
    (speed !== null && speed !== undefined && speed !== '');
  const movementCandidate =
    normalizeCanonicalStatus(movementStatus) ||
    normalizeCanonicalStatus(movementState);
  const resolvedHasEverReported =
    typeof hasEverReported === 'boolean'
      ? hasEverReported
      : lastPacketMs > 0 || hasLiveMotionSignal || statusCandidate === 'inactive';
  const packetIsStale = lastPacketMs > 0 && nowMs - lastPacketMs >= INACTIVE_AFTER_MS;

  if (licenseExpired === true || statusCandidate === 'expired') return 'expired';
  if (!resolvedHasEverReported) return 'no_data';
  if (packetIsStale) return 'inactive';
  if (ignitionValue === true && speedValue > RUNNING_SPEED_KMH) return 'running';
  if (ignitionValue === true) return 'idle';
  if (ignitionValue === false) return 'stopped';
  if (speedValue > RUNNING_SPEED_KMH) return 'running';
  // Stored server labels are a fallback only; fresh ACC/speed determines the
  // live state so an engine-on vehicle at a signal remains Idle, not Stopped.
  if (statusCandidate === 'running' || statusCandidate === 'idle' || statusCandidate === 'stopped') return statusCandidate;
  if (movementCandidate === 'running' || movementCandidate === 'idle' || movementCandidate === 'stopped') return movementCandidate;
  if (statusCandidate === 'inactive') return 'inactive';
  if (statusCandidate === 'no_data') return 'no_data';
  return 'no_data';
};

const pickLicenseExpired = (device = {}, socketData = {}) => {
  const merged = {
    ...(device || {}),
    ...(socketData || {}),
    liveData: {
      ...(device?.liveData || {}),
      ...(device?.deviceLive || {}),
      ...(device?.liveSnapshot || {}),
      ...(socketData?.liveData || {}),
    },
  };
  const expiryCandidate =
    merged?.licenseRuntime?.expiryDate ||
    merged?.licenseRuntime?.licenseExpiryTime ||
    merged?.deviceLicense?.expiryDate ||
    merged?.deviceLicense?.licenseExpiryTime ||
    merged?.licenseExpiryTime ||
    merged?.licenseExpiryDate ||
    merged?.licenseExpiryTs ||
    merged?.expiryDate ||
    merged?.expireAt ||
    merged?.expiresAt ||
    null;
  const expiryMs = toTimestampMs(expiryCandidate, 0);
  const hasFutureExpiry = expiryMs > Date.now();
  const explicitlyActive =
    merged?.licenseRuntime?.isExpired === false ||
    merged?.deviceLicense?.isActive === true;

  if (explicitlyActive || hasFutureExpiry) {
    return false;
  }

  if (
    merged?.licenseRuntime?.isExpired === true ||
    merged?.deviceLicense?.isActive === false ||
    normalizeCanonicalStatus(merged?.status) === 'expired' ||
    normalizeCanonicalStatus(merged?.vehicleStatus) === 'expired'
  ) {
    return true;
  }

  return expiryMs > 0 && expiryMs <= Date.now();
};

export const getCanonicalDeviceStatus = (device = {}, socketData = {}) => {
  const liveData = {
    ...(device?.liveData || {}),
    ...(device?.deviceLive || {}),
    ...(device?.liveSnapshot || {}),
    ...(socketData?.liveData || {}),
  };

  const socketCarriesFreshTelemetry =
    socketData &&
    (
      socketData?.timestamp !== undefined ||
      socketData?.lastUpdate !== undefined ||
      socketData?.correctedTime !== undefined ||
      socketData?.speed !== undefined ||
      socketData?.accStatus !== undefined ||
      socketData?.ignition !== undefined
    );
  const liveDataCarriesFreshTelemetry =
    liveData &&
    (
      liveData?.timestamp !== undefined ||
      liveData?.correctedTime !== undefined ||
      liveData?.speed !== undefined ||
      liveData?.accStatus !== undefined ||
      liveData?.ignition !== undefined
    );

  if (socketCarriesFreshTelemetry) {
    delete liveData.vehicleStatus;
    delete liveData.status;
  }

  if (socketData?.vehicleStatus !== undefined) liveData.vehicleStatus = socketData.vehicleStatus;
  if (socketData?.status !== undefined) liveData.status = socketData.status;

  const lastPacketAt =
    socketData?.serverReceivedAt ??
    socketData?.receivedAt ??
    socketData?.serverTime ??
    socketData?.lastUpdate ??
    socketData?.correctedTime ??
    socketData?.timestamp ??
    liveData?.serverReceivedAt ??
    liveData?.receivedAt ??
    liveData?.serverTime ??
    liveData?.lastUpdate ??
    liveData?.correctedTime ??
    liveData?.timestamp ??
    device?.lastDataReceivedAt ??
    device?.serverReceivedAt ??
    null;

  return resolveCanonicalDeviceStatus({
    status:
      liveData.status ??
      ((socketCarriesFreshTelemetry || liveDataCarriesFreshTelemetry) ? null : device?.status),
    vehicleStatus:
      liveData.vehicleStatus ??
      ((socketCarriesFreshTelemetry || liveDataCarriesFreshTelemetry) ? null : device?.vehicleStatus),
    ignition: socketData?.ignition ?? liveData?.ignition ?? device?.ignition,
    accStatus: socketData?.accStatus ?? liveData?.accStatus ?? device?.accStatus,
    speed: socketData?.speed ?? liveData?.speed ?? device?.speed,
    movementStatus: socketData?.movementStatus ?? liveData?.movementStatus ?? device?.movementStatus,
    movementState: socketData?.movementState ?? liveData?.movementState ?? device?.movementState,
    lastPacketAt,
    hasEverReported:
      lastPacketAt != null || socketCarriesFreshTelemetry || liveDataCarriesFreshTelemetry,
    licenseExpired: pickLicenseExpired(device, socketData),
    now: Date.now(),
  });
};

export const isRunning = (device, socketData = {}) => getCanonicalDeviceStatus(device, socketData) === 'running';
export const isIdle = (device, socketData = {}) => getCanonicalDeviceStatus(device, socketData) === 'idle';
export const isStopped = (device, socketData = {}) => getCanonicalDeviceStatus(device, socketData) === 'stopped';
export const isOffline = (device, socketData = {}) => getCanonicalDeviceStatus(device, socketData) === 'inactive';
